/**
 * StatusSection — on-page magazine lede for the Mini-Project Progress tab.
 *
 * DEV-1079, Chunk A. Full-width row that sits below the KPI shape strip
 * (and, post-Chunk B, above the two-column section pairings). Read-only
 * display surface — the Lead's "post a new status" action lives on the
 * Summary KPI shape click, not on this section.
 *
 * Layout (matches mockups in DEV-1077):
 *   - Header: "Status" + calendar pill (top-right, read mode)
 *   - Narrative (TipTap HTML, serif type, generous line height)
 *   - Lead byline below the narrative
 *   - Photo strip (read mode, click-to-lightbox)
 *
 * Listens for the l4e_miniproject_status_persisted event so it refreshes
 * after the Lead saves a new status from the modal. Same pattern Geo
 * Community Leaders use for the in-flight-edit-vs-public-refresh split.
 *
 * Empty state: project with no statuses yet shows a friendly placeholder
 * inviting the Lead to post the first.
 *
 * Strings localized via `pages:miniProject.status.*` (Chunk D Phase 1
 * i18n sweep, 2026-05-22). Scaffold values exist in all 8 locales with
 * [EN] placeholders for non-English — translator sweep pending.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import StatusCalendarPicker from "../StatusCalendarPicker/StatusCalendarPicker";
import StatusPhotoStrip, { type StatusPhotoItem } from "../StatusPhotoStrip/StatusPhotoStrip";
import {
  fetchMiniProjectStatuses,
  fetchLatestMiniProjectStatus,
  fetchMiniProjectStatus,
  type MiniProjectStatus,
  type MiniProjectStatusSummary,
} from "../../api/miniProjectStatuses";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import styles from "./StatusSection.module.css";
import { sanitizeRichText } from "../../utils/sanitizeHtml";
import ClampedHtml from "../ClampedHtml/ClampedHtml";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  miniProjectGuid: string;
  isLead: boolean;
  /** Lead-only: fired when the Lead clicks the edit pencil. Caller is
   *  expected to open StatusWriter in edit mode with this status. */
  onEditStatus?: (status: MiniProjectStatus) => void;
  /** Lead-only: fired when the Lead clicks the "+ New" button. Caller
   *  opens StatusWriter in create mode (with prefill from latest, if
   *  any). The Summary KPI click is reserved for today's status —
   *  this is the path for back-dated or fresh entries. */
  onNewStatus?: () => void;
}

/* ── Helpers ───────────────────────────────────────────────────── */

function formatLongDate(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
}

/* ── Component ─────────────────────────────────────────────────── */

const StatusSection: React.FC<Props> = ({
  miniProjectGuid,
  isLead,
  onEditStatus,
  onNewStatus,
}) => {
  const { t } = useAppTranslation("pages");

  // ── Data state ──
  const [summaries, setSummaries] = useState<MiniProjectStatusSummary[]>([]);
  const [currentStatus, setCurrentStatus] = useState<MiniProjectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Initial load: summaries + latest status ──
  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [summariesResp, latest] = await Promise.all([
        fetchMiniProjectStatuses(miniProjectGuid, { limit: 200 }),
        fetchLatestMiniProjectStatus(miniProjectGuid),
      ]);
      setSummaries(summariesResp);
      setCurrentStatus(latest);
    } catch (err: any) {
      setLoadError(err?.message || t("miniProject.status.error.load"));
    } finally {
      setLoading(false);
    }
  }, [miniProjectGuid, t]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // ── Refresh on persist event ──
  // The Status writer fires this after a successful save. The section
  // refetches both summaries (new filed date) and latest status (the
  // freshly-posted one).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { miniProjectGuid?: string } | undefined;
      if (!detail?.miniProjectGuid) return;
      if (detail.miniProjectGuid.toUpperCase() !== miniProjectGuid.toUpperCase()) return;
      void loadAll();
    };
    window.addEventListener("l4e_miniproject_status_persisted", handler);
    return () => window.removeEventListener("l4e_miniproject_status_persisted", handler);
  }, [miniProjectGuid, loadAll]);

  // ── Filed dates for the calendar picker ──
  const filedDates = useMemo(() => summaries.map((s) => s.reportingDate), [summaries]);

  // ── Handle date selection from the calendar pill ──
  const handleDateChange = useCallback(
    async (iso: string) => {
      // Find the status summary at this date and fetch its full payload.
      const summary = summaries.find((s) => s.reportingDate === iso);
      if (!summary) return;
      try {
        const full = await fetchMiniProjectStatus(miniProjectGuid, summary.statusGuid);
        setCurrentStatus(full);
      } catch (err: any) {
        setLoadError(err?.message || t("miniProject.status.error.loadForDate"));
      }
    },
    [summaries, miniProjectGuid, t],
  );

  // ── Prev/next nav over filed statuses ──
  // summaries arrive newest-first from the API (controller orders by
  // ReportingDate DESC, PostedAt DESC). So in the array:
  //   summaries[idx + 1] = older (prev chevron, going back in time)
  //   summaries[idx - 1] = newer (next chevron, going forward in time)
  // At boundaries the chevrons disable per Skip 2026-05-28 — better
  // than hiding so non-PMs see the affordance even when it can't fire.
  const navState = useMemo(() => {
    if (!currentStatus || summaries.length === 0) {
      return { canPrev: false, canNext: false, prevIso: null, nextIso: null };
    }
    const idx = summaries.findIndex(
      (s) => s.statusGuid === currentStatus.statusGuid,
    );
    if (idx < 0) {
      return { canPrev: false, canNext: false, prevIso: null, nextIso: null };
    }
    const prevSummary = summaries[idx + 1] ?? null;
    const nextSummary = summaries[idx - 1] ?? null;
    return {
      canPrev: !!prevSummary,
      canNext: !!nextSummary,
      prevIso: prevSummary?.reportingDate ?? null,
      nextIso: nextSummary?.reportingDate ?? null,
    };
  }, [currentStatus, summaries]);

  const handlePrev = useCallback(() => {
    if (navState.prevIso) void handleDateChange(navState.prevIso);
  }, [navState.prevIso, handleDateChange]);

  const handleNext = useCallback(() => {
    if (navState.nextIso) void handleDateChange(navState.nextIso);
  }, [navState.nextIso, handleDateChange]);

  // ── Resolve poster name from security users ──
  const { securityUsers } = useApiSecurityData();
  const { posterName, posterInitials } = useMemo(() => {
    if (!currentStatus) return { posterName: undefined, posterInitials: undefined };
    const guid = currentStatus.postedByUserGuid.toUpperCase();
    const rows = (securityUsers || []) as any[];
    const user = rows.find((u) => String(u.user_guid).toUpperCase() === guid);
    const name: string | undefined =
      user?.display_name || user?.name || user?.screen_name || undefined;
    return { posterName: name, posterInitials: name ? getInitials(name) : "?" };
  }, [currentStatus, securityUsers]);

  // ── Photos as StatusPhotoItem[] ──
  const photos = useMemo<StatusPhotoItem[]>(() => {
    return (currentStatus?.photos ?? []).map((p) => ({
      storageKey: p.storageKey,
      caption: p.caption ?? null,
      position: p.position,
    }));
  }, [currentStatus]);

  // ── Render ──
  return (
    <section className={styles.section} aria-label={t("miniProject.status.sectionAria")}>
      {/* Header — sentence framing + date nav + action buttons.
          Redesigned 2026-05-28 with Skip. Reads as "Status as of <date>"
          for the non-PM audience. Prev/next chevrons disable at the
          oldest / newest filed status. Action buttons hide for non-Leads
          and the empty state replaces the sentence with "No status
          filed yet" plus a single "Post first status" CTA for Leads. */}
      <header className={styles.header}>
        {currentStatus ? (
          <>
            <div className={styles.headerLeft}>
              <h3 className={styles.title}>
                {t("miniProject.status.headerPrefix", { defaultValue: "Status as of" })}
              </h3>
              <div className={styles.dateNav}>
                <button
                  type="button"
                  className={styles.navArrow}
                  disabled={!navState.canPrev}
                  onClick={handlePrev}
                  aria-label={t("miniProject.status.nav.prevAria", {
                    defaultValue: "Previous filed status",
                  })}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="15 6 9 12 15 18" />
                  </svg>
                </button>
                <StatusCalendarPicker
                  value={currentStatus.reportingDate}
                  filedDates={filedDates}
                  mode="read"
                  onChange={handleDateChange}
                  size="md"
                />
                <button
                  type="button"
                  className={styles.navArrow}
                  disabled={!navState.canNext}
                  onClick={handleNext}
                  aria-label={t("miniProject.status.nav.nextAria", {
                    defaultValue: "Next filed status",
                  })}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                </button>
              </div>
            </div>
            {isLead && (onNewStatus || onEditStatus) && (
              <div className={styles.headerActions}>
                {onNewStatus && (
                  <button
                    type="button"
                    className={styles.editBtn}
                    onClick={onNewStatus}
                    title={t("miniProject.status.newTitle")}
                  >
                    <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>
                      +
                    </span>
                    <span>
                      {t("miniProject.status.newButton", { defaultValue: "New status" })}
                    </span>
                  </button>
                )}
                {onEditStatus && (
                  <button
                    type="button"
                    className={styles.editBtn}
                    onClick={() => onEditStatus(currentStatus)}
                    title={t("miniProject.status.editTitle", {
                      date: formatLongDate(currentStatus.reportingDate),
                    })}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                    <span>
                      {t("miniProject.status.editCurrentButton", {
                        defaultValue: "Edit current status",
                      })}
                    </span>
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.headerLeft}>
              <h3 className={styles.title}>
                {t("miniProject.status.empty.title", {
                  defaultValue: "No status filed yet",
                })}
              </h3>
            </div>
            {isLead && onNewStatus && (
              <div className={styles.headerActions}>
                <button
                  type="button"
                  className={styles.editBtn}
                  onClick={onNewStatus}
                >
                  <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>
                    +
                  </span>
                  <span>
                    {t("miniProject.status.empty.postFirst", {
                      defaultValue: "Post first status",
                    })}
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </header>

      {loading && <div className={styles.loadingHint}>{t("miniProject.status.loading")}</div>}

      {!loading && loadError && (
        <div className={styles.errorMsg}>
          {loadError}{" "}
          <button type="button" className={styles.retryBtn} onClick={() => void loadAll()}>
            {t("miniProject.status.retry")}
          </button>
        </div>
      )}

      {!loading && !loadError && !currentStatus && (
        <div className={styles.emptyState}>
          {/* Empty title moved up into the header (sentence-like).
              Body just carries the audience-specific tip / explanation. */}
          {isLead ? (
            <p className={styles.emptyTip}>
              {t("miniProject.status.empty.leadTip", {
                defaultValue:
                  "A status update is how you tell your community what's happening on the project. Post one weekly or whenever progress changes.",
              })}
            </p>
          ) : (
            <p className={styles.emptyHint}>
              {t("miniProject.status.empty.publicHint", {
                defaultValue:
                  "Check back soon — the project Lead will post an update when there's news to share.",
              })}
            </p>
          )}
        </div>
      )}

      {!loading && !loadError && currentStatus && (
        <>
          <ClampedHtml
            className={styles.narrative}
            html={sanitizeRichText(currentStatus.narrativeHtml)}
          />

          <div className={styles.byline}>
            <div className={styles.avatar} aria-hidden="true">
              {posterInitials || "?"}
            </div>
            <span className={styles.bylineName}>
              {posterName || currentStatus.postedByUserGuid.slice(0, 8) + "…"}
            </span>
            <span className={styles.bylineSep}>·</span>
            <span className={styles.bylineMeta}>
              {t("miniProject.status.byline.posted", { date: formatLongDate(currentStatus.reportingDate) })}
            </span>
          </div>

          {photos.length > 0 && (
            <div className={styles.photoBlock}>
              <StatusPhotoStrip mode="read" photos={photos} />
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default StatusSection;
